import { Chart, registerables } from 'chart.js';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useEffect, useMemo, useRef } from 'react';
import { useStocksQuery } from 'components/Stock/Stock.hooks';
import { Stock } from 'components/Stock/Stock.helper';
import { useOutletContext } from 'react-router-dom';
import { useConfigByCountry, usePageTitle } from 'routes/Util/Util.hooks';
import { ContryContextType } from 'routes/CountryPage';
import { countryTextMap } from 'routes/Util/Util.helper';

Chart.register(TreemapController, TreemapElement, ...registerables);

export const TreeMapPage = () => {
  const { countryCode } = useOutletContext<ContryContextType>();
  usePageTitle(`${countryTextMap[countryCode]} 트리맵`);
  const country = countryCode.toLowerCase();
  const ctx = useRef<HTMLCanvasElement>(null);
  const { data: originStocks } = useStocksQuery(country);

  const stocks = useMemo(() => originStocks.filter(({ sector }) => sector !== 'index'), [originStocks]);
  const stockBySymbol = useMemo(() => stocks.reduce<Record<string, Stock>>((acc, stock) => {
    acc[stock.symbol] = stock;
    return acc;
  }, {}), [stocks]);
  const config = useConfigByCountry(country)
  const sectors = useMemo(() => Object.keys(config.titleBySector), [config.titleBySector]);

  useEffect(() => {
    if (!ctx.current || !stocks) {
      return;
    }

    const myChart = new Chart(ctx.current.getContext('2d') as CanvasRenderingContext2D, {
      type: 'treemap',
      data: {
        datasets: [{
          key: 'marketCap',
          groups: ['sector', 'symbol'],
          tree: stocks,
          data: [],
          backgroundColor (ctx: any) {
            if (ctx.type !== 'data') {
              return 'transparent';
            }

            if (sectors.includes(ctx.raw.g)) {
              return 'black';
            }

            const alpha = (Math.abs(stockBySymbol[ctx.raw.g].declineRate) / 25).toFixed(1);
            return `rgb(246, 53, 56, ${alpha})`;
          },
          spacing: 0,
          borderWidth: 0.5,
          borderColor: 'black',
          captions: {
            display: true,
            color: 'white',
            font: {
              size: 12,
            },
            padding: 5,
            formatter: (ctx: any) => {
              return config.titleBySector[ctx.raw.g] || '';
            },
          },
          labels: {
            align: 'center',
            display: true,
            color: 'white',
            font: {
              size: 16,
              weight: 'bold',
            },
            position: 'middle',
            formatter: (ctx: any) => {
              const symbol: string = ctx.raw.g;
              if (!stockBySymbol[symbol]) return '';
              return `${stockBySymbol[symbol].krShortName}`;
            },
          }
        }]
      },
      options: {
        animation: false,
        responsive: true,
        aspectRatio: window.screen.availWidth > window.screen.availHeight ? 2 : 1,
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false
          },
          tooltip: {
            position: 'nearest',
            titleColor: 'black',
            bodyColor: 'black',
            borderColor: 'black',
            borderWidth: 1,
            backgroundColor: 'white',
            titleFont: {
              size: 16,
            },
            bodyFont: {
              size: 16,
              weight: 'bold',
            },
            bodySpacing: 3,
            padding: {
              left: 10,
              right: 10,
              top: 10,
              bottom: 10,
            },
            caretSize: 0,
            cornerRadius: 0,
            displayColors: false,
            callbacks: {
              title: (tooltipItems: any) => {
                const symbol = tooltipItems[tooltipItems.length - 1].raw.g;
                if (!stockBySymbol[symbol]) {
                  return '';
                }

                return stockBySymbol[symbol].krShortName;
              },
              label: function(context: any) {
                const symbol = context.raw.g;
                if (!stockBySymbol[symbol]) {
                  return '';
                }

                const { declineRate } = stockBySymbol[symbol];

                return [declineRate.toFixed(1) + '%'];
            }
            }
          }
        }
      }
  });
    return () => {
      myChart.destroy();
    }
  }, [config.titleBySector, ctx, sectors, stockBySymbol, stocks])
  return (
    <>
      <Row>
        <Col>
          <canvas ref={ctx} />
        </Col>
      </Row>
      <Row>
        <Col>
          <dl className="d-flex justify-content-end">
            <dt className="text-muted ms-3 me-2">면적</dt>
            <dd className="text-muted">시가총액</dd>
            <dt className="text-muted ms-3 me-2">색상</dt>
            <dd className="text-muted">하락률 크기</dd>
          </dl>
        </Col>
      </Row>
    </>
  )
}
